import { useEffect } from 'react';
import * as R from 'remeda';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import * as apiClient from '@rossum/api-client';
import { setRossumAppDomainCookie } from '../../appDomain';
import { apiUrl } from '../../constants/config';
import { getAuthCodeFromLocation, ReferrerState } from '../../lib/auth';
import { authSignIn } from '../../redux/modules/auth/actions';
import { throwError } from '../../redux/modules/messages/actions';

export const useSignInWithAuthCodeFromUrl = ({
  onAuthFinish,
}: { onAuthFinish?: () => void } = {}) => {
  const location = useLocation<ReferrerState>();
  const dispatch = useDispatch();
  const { mutateAsync, isLoading, isError } = useMutation({
    mutationFn: (token: string) => {
      // temporary API client with token from URL
      const api = apiClient.createElisClient({
        baseUrl: apiUrl,
        getAuthToken: () => token,
      });

      return api.request(
        apiClient.endpoints.authentication.token({ origin: 'rossum_ui' })
      );
    },
  });

  const token = getAuthCodeFromLocation(location);

  useEffect(() => {
    if (!!token && R.isString(token)) {
      mutateAsync(token)
        .then(({ key, domain }) => {
          setRossumAppDomainCookie(domain);
          dispatch(authSignIn(key));
          onAuthFinish?.();
        })
        .catch(() => {
          dispatch(throwError('authError'));
          onAuthFinish?.();
        });
    }
  }, [token, dispatch, mutateAsync, onAuthFinish]);

  // Signing in as soon as an auth code is present, not only while the mutation
  // is mid-flight — it fires in an effect (after first render) and resets to idle
  // on success before navigation, so keying purely on `isLoading` would flash the
  // classic login form. Fall back to the form only when the exchange errors.
  const exchangingToken = !!token && !isError;

  return { isSigningIn: isLoading || exchangingToken };
};
